import { endpoints } from "@/config/endpoints";

interface LoginCredentials {
  phone: string;
  password: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  roles: string[];
  permissions: string[];
}

interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

interface ErrorResponse {
  error: string;
}

export const STORAGE_KEY = {
  TOKEN: "auth_token",
  REFRESH_TOKEN: "refresh_token",
  USER: "auth_user",
} as const;

// Allowed roles for the admin dashboard
const ALLOWED_ROLES = ["admin", "director", "quantity_surveyor", "procurement"];

export const authService = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    console.log("Attempting login with credentials:", credentials);

    const response = await fetch(endpoints.auth.login, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();
    console.log("Login response:", data);

    // Check for error in response data
    if (data.error) {
      console.error("Login error:", data.error);
      throw new Error(data.error);
    }

    // Check if user exists in response
    if (!data.user) {
      console.error("No user in response");
      throw new Error("Invalid credentials");
    }

    // Check if user has any of the allowed roles
    const hasAllowedRole = data.user.roles.some((role) =>
      ALLOWED_ROLES.includes(role)
    );
    if (!hasAllowedRole) {
      console.error("User does not have allowed roles");
      throw new Error("Access denied");
    }

    // Store both tokens and user data
    localStorage.setItem(STORAGE_KEY.TOKEN, data.accessToken);
    localStorage.setItem(STORAGE_KEY.REFRESH_TOKEN, data.refreshToken);
    localStorage.setItem(STORAGE_KEY.USER, JSON.stringify(data.user));

    return {
      user: data.user,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    };
  },

  refreshToken: async (): Promise<string> => {
    const refreshToken = localStorage.getItem(STORAGE_KEY.REFRESH_TOKEN);
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await fetch(endpoints.auth.refresh, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      throw new Error("Failed to refresh token");
    }

    const data = await response.json();
    localStorage.setItem(STORAGE_KEY.TOKEN, data.accessToken);
    return data.accessToken;
  },

  getCurrentUser: async (token: string) => {
    // First try to get from localStorage
    const storedUser = localStorage.getItem(STORAGE_KEY.USER);
    if (storedUser) {
      const user = JSON.parse(storedUser);
      // Verify the user has at least one of the allowed roles
      const hasAllowedRole = user.roles.some((role) =>
        ALLOWED_ROLES.includes(role)
      );
      if (hasAllowedRole) {
        return user;
      }
    }

    // If no stored user or not authorized, fetch from API
    const response = await fetch(endpoints.auth.me, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired, try to refresh
        try {
          const newToken = await authService.refreshToken();
          // Retry the request with new token
          const retryResponse = await fetch(endpoints.auth.me, {
            headers: {
              Authorization: `Bearer ${newToken}`,
            },
          });
          if (!retryResponse.ok) {
            throw new Error("Failed to get current user after token refresh");
          }
          const userData = await retryResponse.json();
          localStorage.setItem(STORAGE_KEY.USER, JSON.stringify(userData));
          return userData;
        } catch (error) {
          // If refresh fails, logout
          authService.logout();
          throw new Error("Session expired. Please login again.");
        }
      }
      throw new Error("Failed to get current user");
    }

    const userData = await response.json();
    localStorage.setItem(STORAGE_KEY.USER, JSON.stringify(userData));
    return userData;
  },

  logout: () => {
    localStorage.removeItem(STORAGE_KEY.TOKEN);
    localStorage.removeItem(STORAGE_KEY.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEY.USER);
  },

  getStoredAuth: (): { token: string | null; user: User | null } => {
    const token = localStorage.getItem(STORAGE_KEY.TOKEN);
    const userStr = localStorage.getItem(STORAGE_KEY.USER);

    return {
      token,
      user: userStr ? JSON.parse(userStr) : null,
    };
  },

  // Check if the user has a specific role
  hasRole: (role: string): boolean => {
    const userStr = localStorage.getItem(STORAGE_KEY.USER);
    if (!userStr) return false;

    const user = JSON.parse(userStr);
    return user.roles.includes(role);
  },

  // Check if user has any of the provided roles
  hasAnyRole: (roles: string[]): boolean => {
    const userStr = localStorage.getItem(STORAGE_KEY.USER);
    if (!userStr) return false;

    const user = JSON.parse(userStr);
    return roles.some((role) => user.roles.includes(role));
  },

  // Additional auth-related methods can be added here
  // Example:
  // register: async (userData: RegisterUserData) => {
  //   const response = await fetch(endpoints.auth.register, {
  //     method: "POST",
  //     headers: {
  //       "Content-Type": "application/json",
  //     },
  //     body: JSON.stringify(userData),
  //   });
  //   return response.json();
  // },
};
